import React from 'react'
import { Box, Divider, Flex, styled, Text } from '@chilipiper/design-system'
import { IconButton, UnstyledButton } from '@chilipiper/design-system/src/new'
import { SvgArrowLeft, SvgArrowRight } from '@chilipiper/icons/src/design-system'
import {
  addDays,
  format,
  startOfDay,
  startOfWeek,
  differenceInDays,
  formatInTimeZone,
} from '@chilipiper/date-time'
import { useLocale } from '@chilipiper/locale/src/new'

import { useTranslations } from '../../../context/TranslationContext'
import { customerColorCss } from './styles'

type Props = {
  availableDays: Record<string, boolean>
  daysWrapperRef: React.RefObject<HTMLDivElement | null>
  onSelectDay: (day: string) => void
  selectedDay?: string
  suggestedTimesToggle?: React.ReactNode
  timezone: string
}

const getWeekDayLabels = (date: Date, locale = 'en_US') => {
  return {
    month: format(date, 'MMM', { locale }),
    weekDay: format(date, 'EEE', { locale }),
    dayNumber: format(date, 'd', { locale }),
  }
}

export const Days = ({
  daysWrapperRef,
  onSelectDay,
  availableDays,
  selectedDay,
  suggestedTimesToggle,
  timezone,
}: Props) => {
  let lastAvailableDay: string | undefined
  for (const day of Object.keys(availableDays)) {
    if (!lastAvailableDay || day > lastAvailableDay) {
      lastAvailableDay = day
    }
  }
  const locale = useLocale()
  const [dayBookHeader] = useTranslations(['dayBookHeader'])

  const daysToLastAvailableDay = lastAvailableDay
    ? differenceInDays(
        new Date(lastAvailableDay),
        new Date(formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd'))
      )
    : 0
  return (
    <>
      <Flex gap={2} p={5} pr={3} alignItems='center' justifyContent='space-between'>
        <Flex flexDirection='column' gap={2}>
          <Text textStyle='heading-s'>{dayBookHeader ?? 'What day is best for you?'}</Text>
          {suggestedTimesToggle}
        </Flex>
        <Flex gap={1}>
          <IconButton
            icon={SvgArrowLeft}
            size={7}
            label='Previous Week'
            variant='moderate'
            onClick={() => {
              if (daysWrapperRef.current) {
                daysWrapperRef.current.scrollBy({
                  left: -daysWrapperRef.current.offsetWidth + 16,
                  behavior: 'smooth',
                })
              }
            }}
          />
          <IconButton
            icon={SvgArrowRight}
            size={7}
            label='Next Week'
            variant='moderate'
            onClick={() => {
              if (daysWrapperRef.current) {
                daysWrapperRef.current.scrollBy({
                  left: daysWrapperRef.current.offsetWidth - 16,
                  behavior: 'smooth',
                })
              }
            }}
          />
        </Flex>
      </Flex>

      <Divider />
      <DaysWrapper overflow='auto' ref={daysWrapperRef}>
        <Flex flexDirection='column' flexShrink={0} w={2} pt='30px'>
          <Divider />
          <Box flexGrow={1} bg='bg/button-moderate' />
        </Flex>
        {Array(Math.max(21, daysToLastAvailableDay + 1) + 6)
          .fill(undefined)
          .map((_, i) => {
            const startOfThisWeek = startOfWeek(new Date())
            const day = addDays(startOfDay(new Date()), i - 6)
            if (day < startOfThisWeek) {
              return null
            }
            const { weekDay, month, dayNumber } = getWeekDayLabels(day, locale)
            const dayString = format(day, 'yyyy-MM-dd')
            const isAvailable = availableDays[dayString]
            return (
              <DayContainer
                key={dayString}
                data-day={dayString}
                w='calc(14.2857% - 2.2857px)' // calc(100% - 16px) / 7
                flexShrink={0}
              >
                <Text
                  display='block'
                  textAlign='center'
                  color='text/body-moderate'
                  fontSize={12}
                  lineHeight={2}
                  fontWeight={500}
                  my={2}
                >
                  {weekDay}
                </Text>
                <Divider />

                <DayButton
                  textAlign='center'
                  w='100%'
                  py='15px'
                  onClick={() => onSelectDay(dayString)}
                  disabled={!isAvailable}
                  isSelected={dayString === selectedDay}
                >
                  <Text color='inherit' fontSize={16} fontWeight={600} display='block' mb={1}>
                    {dayNumber}
                  </Text>
                  <Text color='inherit' fontSize={12} lineHeight={2} fontWeight={500}>
                    {month}
                  </Text>
                </DayButton>
              </DayContainer>
            )
          })}
        <Flex flexDirection='column' flexShrink={0} w={2} pt='30px'>
          <Divider />
          <Box flexGrow={1} bg='bg/button-moderate' />
        </Flex>
      </DaysWrapper>
    </>
  )
}

const DayButton = styled(UnstyledButton)<{ isSelected: boolean }>`
  background: ${({ theme }) => theme.colors['bg/button-moderate']};
  color: ${({ theme }) => theme.colors['text/action-moderate']};
  transition:
    background-color 0.2s,
    color 0.2s;

  ${({ isSelected }) => (isSelected ? customerColorCss : undefined)}
  :hover,
    :focus {
    ${customerColorCss}
  }

  :disabled {
    background: ${({ theme }) => theme.colors['bg/button-moderate']};
    color: ${({ theme }) => theme.colors['text/disabled']};
  }
`

const DaysWrapper = styled(Flex)`
  scroll-snap-type: x mandatory;
  scroll-padding-left: 8px;
  overflow-x: auto;
  overflow-y: hidden;
`

const DayContainer = styled(Box)`
  scroll-snap-align: start;
`
